import { useEffect, useState, type FormEvent } from 'react'
import { usePageData } from '@/hooks/usePageData'
import { FORMS, submitAuthForm, AuthSubmissionError } from '@/lib/authForm'
import { navigateTo } from '@/lib/navigate'
import FlipButton from '@/components/ui/FlipButton'

/*
  MIG Password Reset page. Three states:
    1. Success — flash_notice set: confirmation + link to /sign-in
    2. Invalid token (or no user found): error + link to /forgot-password
    3. Default: form with new password + confirm

  The platformOS `modules/portal/password_reset` form expects top-level
  fields email/password/password_confirmation, with the `id` of the
  user already baked into the form HTML (via include_form's id param).

  Styling lives in src/styles/pages/auth.css.
*/

interface PasswordResetPayload {
  email: string
  token: string
  token_valid: boolean
  has_user: boolean
  flash_notice: string | null
  flash_alert: string | null
}

export default function PasswordReset() {
  const payload = usePageData<PasswordResetPayload>() ?? {
    email: '',
    token: '',
    token_valid: false,
    has_user: false,
    flash_notice: null,
    flash_alert: null,
  }
  const initialSuccess = !!payload.flash_notice
  const tokenInvalid = !initialSuccess && (!payload.has_user || !payload.token_valid)

  const [visible, setVisible] = useState(false)
  const [success, setSuccess] = useState(initialSuccess)
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(payload.flash_alert ?? null)

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100)
    return () => clearTimeout(t)
  }, [])

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (password !== confirm) {
      setError("Password and confirmation don't match.")
      return
    }
    if (!FORMS.passwordReset) {
      setError('Reset form is not available. Please refresh and try again.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await submitAuthForm('passwordReset', {
        'form[email]': payload.email,
        'form[password]': password,
        'form[password_confirmation]': confirm,
      })
      setSuccess(true)
    } catch (err) {
      const msg =
        err instanceof AuthSubmissionError ? err.message : 'Could not update password. Please try again.'
      setError(msg)
      setSubmitting(false)
    }
  }

  return (
    <section className="mig-page-section">
      <div className="mig-page-title-row" data-visible={visible}>
        <h1 className="mig-password-reset-page-title">
          {success ? 'Password updated' : tokenInvalid ? 'Reset link expired' : 'Reset password'}
        </h1>
      </div>

      {success && (
        <div className="mig-auth-state-body">
          <p className="mig-auth-body">
            {payload.flash_notice ?? 'Your password has been updated. Sign in with your new password.'}
          </p>
          <FlipButton
            onClick={navigateTo('/sign-in')}
            className="mig-auth-cta-link"
          >
            Sign in
          </FlipButton>
        </div>
      )}

      {tokenInvalid && (
        <div className="mig-auth-state-body">
          <p className="mig-auth-body">
            This reset link is no longer valid. Reset links expire after a short time. Request a
            new link below and we will send you a fresh email.
          </p>
          <FlipButton
            onClick={navigateTo('/forgot-password')}
            className="mig-auth-submit mig-auth-cta-link"
          >
            Request a new link
          </FlipButton>
        </div>
      )}

      {!success && !tokenInvalid && (
        <form
          onSubmit={handleSubmit}
          noValidate
          className="mig-form mig-auth-form"
        >
          <p className="mig-auth-body">
            Resetting password for <strong>{payload.email}</strong>.
          </p>

          <div className="mig-auth-field-row">
            <div className="mig-auth-field-inner">
              <label className="mig-auth-field-label">NEW PASSWORD</label>
              <input
                type="password"
                name="password"
                autoComplete="new-password"
                placeholder="At least 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
                className="mig-auth-input"
              />
            </div>
            <div className="mig-divider-line" />
          </div>

          <div className="mig-auth-field-row">
            <div className="mig-auth-field-inner">
              <label className="mig-auth-field-label">CONFIRM PASSWORD</label>
              <input
                type="password"
                name="password_confirmation"
                autoComplete="new-password"
                placeholder="Re-enter your new password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                disabled={submitting}
                className="mig-auth-input"
              />
            </div>
            <div className="mig-divider-line" />
          </div>

          <div className="mig-auth-action-row">
            {error && <p role="alert" className="mig-auth-error">{error}</p>}
            <FlipButton
              type="submit"
              disabled={submitting}
              className="mig-auth-submit"
              data-pending={submitting}
            >
              {submitting ? 'Updating…' : 'Update password'}
            </FlipButton>
          </div>

          <div className="mig-page-back-row">
            <a href="/sign-in" className="mig-auth-back">BACK</a>
            <div className="mig-divider-line" />
          </div>
        </form>
      )}
    </section>
  )
}
